import { Component, OnChanges, Input, SimpleChanges, OnInit, Injector } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FcItemModalContext } from '../../../models';
import { FcInsurancePlanChcTabGroup, FcInsurancePlanListItemDto, FormGroupValidationRules } from 'webcommon/shared';
import { debounceTime } from 'rxjs/operators';
import { FcItemListBaseComponent } from '../../generic';

@Component({
    providers: [ApAlertService, FcListValidationService],
    selector: 'fc-insurance-plan-chc-tab',
    styleUrls: ['./fc-insurance-plan-chc-tab.component.less'],
    templateUrl: './fc-insurance-plan-chc-tab.component.html',
})
export class FcInsurancePlanCHCTabComponent extends FcItemListBaseComponent implements OnChanges, OnInit {
    @Input() baseId: string;
    @Input() form: FormGroup;
    @Input() listItem: FcInsurancePlanListItemDto;
    @Input() isSubmitted = false;
    @Input() itemContext: FcItemModalContext;
    @Input() isDataLoaded: boolean;

    private readonly waitTime: number = 300;
    public selectedChcSettingsPks: string[] = [];

    // Form Controls
    public chcSettingsUidControl: FormControl = new FormControl('');
    public federallyQualifiedHealthCenterControl: FormControl = new FormControl(false);
    public slidingFeeCarrierControl: FormControl = new FormControl(false);
    public fileClaimsControl: FormControl = new FormControl(false);
    public autoAdjustVisitsControl: FormControl = new FormControl(false);
    public insuredAlwaysSameAsPatientControl: FormControl = new FormControl(false);
    public medicareAdvantageCarrierControl: FormControl = new FormControl(false);
    public bypassPrimaryFilingControl: FormControl = new FormControl(false);

    public listItemDefaultValidation: FormGroupValidationRules = {
        ChcSettings: {
            IsRequired: false,
        },
        ApprovalSettingUid: {
            IsRequired: false,
        },
    };

    constructor(
        injector: Injector,
    ) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemContext != null) {
            this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
        }
        if (changes.form != null) {
            this.addControlsToForm();
        }
        if ((changes.itemContext != null || changes.form != null || changes.listItem != null) && this.isDataLoaded) {
            this.setForm();
        }
    }

    public ngOnInit(): void {
        this.slidingFeeCarrierControl.valueChanges
            .pipe(debounceTime(this.waitTime), this.takeUntilDestroy()).subscribe((value: boolean) => {
                if (value) {
                    this.fileClaimsControl.enable();
                } else {
                    this.fileClaimsControl.disable();
                    this.fileClaimsControl.setValue(false);
                }
            });

        this.medicareAdvantageCarrierControl.valueChanges
            .pipe(debounceTime(this.waitTime), this.takeUntilDestroy()).subscribe((value: boolean) => {
                if (value) {
                    this.insuredAlwaysSameAsPatientControl.setValue(true);
                }
            });
    }

    private addControlsToForm(): void {
        this.form.addControl('chcSettingsUid', this.chcSettingsUidControl);
        this.form.addControl('federallyQualifiedHealthCenter', this.federallyQualifiedHealthCenterControl);
        this.form.addControl('slidingFeeCarrier', this.slidingFeeCarrierControl);
        this.form.addControl('fileClaims', this.fileClaimsControl);
        this.form.addControl('autoAdjustVisits', this.autoAdjustVisitsControl);
        this.form.addControl('insuredAlwaysSameAsPatient', this.insuredAlwaysSameAsPatientControl);
        this.form.addControl('medicareAdvantageCarrier', this.medicareAdvantageCarrierControl);
        this.form.addControl('bypassPrimaryFiling', this.bypassPrimaryFilingControl);

    }

    public setForm(): void {
        const values: FcInsurancePlanChcTabGroup = { ...this.listItem.InsurancePlanChc };

        // Federally Qualified Health Center Control
        this.federallyQualifiedHealthCenterControl.setValue(values.FQHCCarrier);

        // Sliding Fee Carrier Control
        this.slidingFeeCarrierControl.setValue(values.SFCarrier);

        // File Claims Control
        this.fileClaimsControl.setValue(values.SFFileClaims);
        if (!this.slidingFeeCarrierControl.value) {
            this.fileClaimsControl.disable();
        }

        // Auto Adjust Visits Control
        this.autoAdjustVisitsControl.setValue(values.AutoAdjustVisits);

        // Insured Always Same As Patient Control
        this.insuredAlwaysSameAsPatientControl.setValue(values.InsuredAlwaysSameAsPatient);

        // Medicare Advantage Carrier Control
        this.medicareAdvantageCarrierControl.setValue(values.MBAdvantageInsCarrier);

        // Bypass Primary Filing Control
        this.bypassPrimaryFilingControl.setValue(values.Secondary_BypassPrimaryFiling);

        this.selectedChcSettingsPks = this.getFindControlValue(this.listItem.InsurancePlanChc?.ApprovalSettingUid);
    }


    public getFindControlValidation(controlName: string): boolean {
        if (this.fcListValidation.validations[controlName]) {
            return !!this.fcListValidation.validations[controlName].IsRequired;
        } else {
            return !!this.listItemDefaultValidation[controlName].IsRequired;
        }
    }
}
